import React, { useState, useEffect } from 'react';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

function getTimeLeft(target: string | Date): TimeLeft {
  const total = new Date(target).getTime() - Date.now();
  if (total <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  return {
    total,
    days: Math.floor(total / (1000 * 60 * 60 * 24)),
    hours: Math.floor((total / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((total / 1000 / 60) % 60),
    seconds: Math.floor((total / 1000) % 60),
  };
}

interface CountdownTimerProps {
  target: string | Date;
  /** 'card' = compact inline blocks, 'hero' = large emphasis blocks */
  variant?: 'card' | 'hero';
  /** Use light text colors (for dark overlays) */
  onDark?: boolean;
  /** Larger text for card variant */
  large?: boolean;
  subtext?: string;
  onExpire?: () => void;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ target, variant = 'card', onDark = false, large = false, subtext, onExpire }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(getTimeLeft(target));

  useEffect(() => {
    const tick = () => {
      const t = getTimeLeft(target);
      setTimeLeft(t);
      if (t.total <= 0) onExpire?.();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  if (timeLeft.total <= 0) return (
    <span className={variant === 'hero' ? 'text-amber-300 font-bold text-lg' : 'text-xs text-amber-600 dark:text-amber-400 font-semibold'}>
      Draw imminent
    </span>
  );

  if (variant === 'hero') {
    const units = [
      { label: 'Days', value: timeLeft.days },
      { label: 'Hours', value: timeLeft.hours },
      { label: 'Mins', value: timeLeft.minutes },
      { label: 'Secs', value: timeLeft.seconds },
    ];
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-3">
          {units.map(({ label, value }, i) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center">
                <div className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 min-w-[64px] text-center">
                  <span className="text-3xl font-extrabold text-white tabular-nums">{String(value).padStart(2, '0')}</span>
                </div>
                <span className="text-xs text-indigo-200 mt-1 font-medium">{label}</span>
              </div>
              {i < units.length - 1 && <span className="text-2xl font-bold text-white/50 mb-4">:</span>}
            </React.Fragment>
          ))}
        </div>
        {subtext && <p className="text-xs text-indigo-200 mt-1">{subtext}</p>}
      </div>
    );
  }

  // card variant — compact inline
  const show = timeLeft.days > 0
    ? `${timeLeft.days}d ${String(timeLeft.hours).padStart(2, '0')}h ${String(timeLeft.minutes).padStart(2, '0')}m`
    : `${String(timeLeft.hours).padStart(2, '0')}h ${String(timeLeft.minutes).padStart(2, '0')}m ${String(timeLeft.seconds).padStart(2, '0')}s`;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className={`inline-block rounded-full animate-pulse ${large ? 'w-2 h-2' : 'w-1.5 h-1.5'} ${onDark ? 'bg-emerald-400' : 'bg-indigo-500'}`} />
        <span className={`font-bold tabular-nums ${large ? 'text-xl' : 'text-sm'} ${onDark ? 'text-white' : 'text-indigo-700 dark:text-indigo-300'}`}>{show}</span>
      </div>
      {subtext && <p className={`text-xs pl-3 ${onDark ? 'text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>{subtext}</p>}
    </div>
  );
};
